import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Switch, FormControlLabel } from '@mui/material';
import NodeSelect from './NodeSelect';
import NodeCodeEditor from './NodeCodeEditor';
import NodeKeyValueRow from './NodeKeyValueRow';
import NodeAddButton from './NodeAddButton';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeWebhookResponseConfig — Webhook custom response configuration.
 * Used by: Webhook.
 *
 * Configures: enable custom response, status code, content type,
 * response headers, and response body.
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.md,
  width: '100%',
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  userSelect: 'none',
}));

const StyledSwitch = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: -4,
  '& .MuiTypography-root': {
    fontSize: NODE_UI.fontSize.value,
    color: theme.palette.text.primary,
  },
  '& .MuiSwitch-root': {
    transform: 'scale(0.85)',
  },
}));

const STATUS_OPTIONS = [
  { value: '200', label: '200 OK' },
  { value: '201', label: '201 Created' },
  { value: '202', label: '202 Accepted' },
  { value: '204', label: '204 No Content' },
  { value: '400', label: '400 Bad Request' },
  { value: '401', label: '401 Unauthorized' },
  { value: '403', label: '403 Forbidden' },
  { value: '404', label: '404 Not Found' },
  { value: '500', label: '500 Internal Server Error' },
];

const CONTENT_TYPE_OPTIONS = [
  { value: 'application/json', label: 'JSON' },
  { value: 'text/plain', label: 'Plain Text' },
  { value: 'text/html', label: 'HTML' },
  { value: 'application/xml', label: 'XML' },
];

const NodeWebhookResponseConfig = React.memo(function NodeWebhookResponseConfig({
  label = 'Custom Response',
  enabled = false,
  onEnabledChange,
  statusCode = '200',
  onStatusCodeChange,
  contentType = 'application/json',
  onContentTypeChange,
  headers = [],
  onHeadersChange,
  body = '',
  onBodyChange,
  sx,
}) {
  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      <StyledSwitch
        control={
          <Switch
            checked={enabled}
            onChange={(e) => onEnabledChange?.(e.target.checked)}
            size="small"
          />
        }
        label={label}
      />

      {enabled && (
        <>
          <NodeSelect
            label="Status Code"
            value={statusCode}
            onChange={onStatusCodeChange}
            options={STATUS_OPTIONS}
          />

          <NodeSelect
            label="Content Type"
            value={contentType}
            onChange={onContentTypeChange}
            options={CONTENT_TYPE_OPTIONS}
          />

          <div>
            <Label>Response Headers</Label>
            {headers.map((header, index) => (
              <NodeKeyValueRow
                key={header._id || index}
                keyValue={header.key}
                valueValue={header.value}
                onKeyChange={(val) => {
                  const next = [...headers];
                  next[index] = { ...header, key: val };
                  onHeadersChange?.(next);
                }}
                onValueChange={(val) => {
                  const next = [...headers];
                  next[index] = { ...header, value: val };
                  onHeadersChange?.(next);
                }}
                onDelete={() => onHeadersChange?.(headers.filter((_, i) => i !== index))}
                showLabels={index === 0}
              />
            ))}
            <NodeAddButton
              label="Add Header"
              onClick={() => onHeadersChange?.([...headers, { key: '', value: '' }])}
            />
          </div>

          <NodeCodeEditor
            label="Response Body"
            value={body}
            onChange={onBodyChange}
            language={contentType === 'application/json' ? 'json' : 'text'}
            height="120px"
          />
        </>
      )}
    </Root>
  );
});

export default NodeWebhookResponseConfig;
